import { normalizeThemeFont, syncThemeFontStylesheet } from '~/utils/theme-font'

interface PreviewThemeStyle {
  themeId?: number
  static?: {
    css?: string
    frame?: string
  }
  primaryColor?: { red: number, green: number, blue: number }
  secondaryColor?: { red: number, green: number, blue: number }
  font?: { fontFamily: string, cdn?: string }
}

interface PreviewThemeMessage {
  type?: string
  themeStyle?: PreviewThemeStyle
}

const removeLeakedRuntimeConfig = () => {
  const textNodes = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT)
  const leakedNodes: Text[] = []
  let node: Text | null

  while ((node = textNodes.nextNode() as Text | null)) {
    const text = node.textContent || ''
    // Nuxt may split the leaked payload across multiple text nodes, so do
    // not require all markers to exist in the same node.
    const isRuntimeConfig =
      text.includes('botDetection') ||
      text.includes('buildAssetsDir') ||
      text.includes('pageMetaRobots')
    const isInsideScript = node.parentElement?.closest('script')

    if (isRuntimeConfig && !isInsideScript) {
      leakedNodes.push(node)
    }
  }

  leakedNodes.forEach(node => node.remove())
}

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()

  // Nuxt can briefly render the runtime payload as text while the preview
  // page is bootstrapping. Remove it in every preview context, not only when
  // PREVIEW_STATIC is enabled, and keep watching because the payload can be
  // injected after the initial plugin execution.
  removeLeakedRuntimeConfig()
  const runtimeConfigObserver = new MutationObserver(removeLeakedRuntimeConfig)
  runtimeConfigObserver.observe(document.body, { childList: true, subtree: true })
  window.addEventListener('beforeunload', () => runtimeConfigObserver.disconnect(), { once: true })

  if (window.parent !== window) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        removeLeakedRuntimeConfig()
        window.parent.postMessage({ type: 'webfaster:preview-ready' }, '*')
      })
    })
  }

  if (!config.public.PREVIEW_STATIC) {
    return
  }

  const themeStyleStore = useThemeStyleStore()

  window.addEventListener('message', async (event: MessageEvent<PreviewThemeMessage>) => {
    if (event.source !== window.parent || event.data?.type !== 'webfaster:preview-theme' || !event.data.themeStyle) {
      return
    }

    const themeStyle = event.data.themeStyle
    themeStyleStore.update({
      themeId: themeStyle.themeId ?? themeStyleStore.themeId,
      css: themeStyle.static?.css ?? themeStyleStore.css,
      frame: themeStyle.static?.frame ?? themeStyleStore.frame,
      primaryColor: themeStyle.primaryColor ?? themeStyleStore.primaryColor,
      secondaryColor: themeStyle.secondaryColor ?? themeStyleStore.secondaryColor,
      font: normalizeThemeFont(themeStyle.font ?? themeStyleStore.font),
    })

    await syncThemeFontStylesheet(themeStyleStore.font)
    window.parent.postMessage({ type: 'webfaster:preview-ready' }, '*')
  })
})
